import {
  addDoc,
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import type { AppNotification } from '../../types/notifications';

function docToNotification(id: string, data: Record<string, unknown>): AppNotification {
  return {
    id,
    title: (data.title as string) || '',
    message: (data.message as string) || '',
    type: (data.type as string) || 'general',
    status: data.status as string,
    timestamp: data.timestamp as Timestamp,
    isRead: data.isRead === true,
    navigateTo: data.navigateTo as string,
    recipientId: data.recipientId as string,
    senderId: data.senderId as string,
    senderName: data.senderName as string,
    relatedEntityId: data.relatedEntityId as string,
    relatedEntityType: data.relatedEntityType as string,
    metadata: (data.metadata as Record<string, string>) || {},
  };
}

export function subscribeNotifications(
  userId: string,
  onUpdate: (notifications: AppNotification[]) => void,
): () => void {
  const q = query(
    collection(db, 'users', userId, 'notifications'),
    orderBy('timestamp', 'desc'),
    limit(100),
  );
  
  return onSnapshot(
    q,
    (snap) => {
      onUpdate(snap.docs.map((d) => docToNotification(d.id, d.data() as Record<string, unknown>)));
    },
    (error) => {
      console.error('subscribeNotifications failed', error);
      onUpdate([]);
    },
  );
}

export async function listNotifications(userId: string): Promise<AppNotification[]> {
  const q = query(
    collection(db, 'users', userId, 'notifications'),
    orderBy('timestamp', 'desc'),
    limit(100),
  );
  const snap = await getDocs(q);
  
return snap.docs.map((d) => docToNotification(d.id, d.data() as Record<string, unknown>));
}

export async function markNotificationRead(userId: string, notificationId: string): Promise<void> {
  await updateDoc(doc(db, 'users', userId, 'notifications', notificationId), { isRead: true });
}

export async function markAllNotificationsRead(userId: string, ids: string[]): Promise<void> {
  await Promise.all(ids.map((id) => markNotificationRead(userId, id)));
}

/** Writes inbox doc; Cloud Function `onNotificationCreated` delivers FCM. */
export async function sendNotification(
  userId: string,
  notification: Omit<AppNotification, 'id'> & { id?: string },
): Promise<string> {
  const ref = await addDoc(collection(db, 'users', userId, 'notifications'), {
    title: notification.title,
    message: notification.message,
    type: notification.type,
    status: notification.status || '',
    timestamp: notification.timestamp || Timestamp.now(),
    isRead: notification.isRead ?? false,
    navigateTo: notification.navigateTo || '',
    recipientId: userId,
    senderId: notification.senderId || '',
    senderName: notification.senderName || '',
    relatedEntityId: notification.relatedEntityId || '',
    relatedEntityType: notification.relatedEntityType || '',
    metadata: notification.metadata || {},
  });

  return ref.id;
}
